import { get } from "@rails/request.js"

import ComboBoxController from "./combo_box_controller"

import {
  formatSiteAddressLine,
  showsFallbackAddress,
} from "../../lib/format-site-address-line"

// Connects to data-controller="ui--entity-selector"
// Async single-select entity picker, built ON TOP of the ComboBox controller — the
// Hotwire equivalent of how the React EntitySelector wraps ComboBox. It reuses all of
// ComboBox's popover / selection / keyboard logic (via inheritance) and overrides only
// the filtering: instead of filtering a static client-side list, it fetches matches from
// `search_url` (debounced), paginates with skip/take + dedup, and preserves the selected
// option in results. Uses @rails/request.js per CONEK patterns.
//
// Page params: when the first page of a search (skip 0) answers with an object
// carrying `page_params` (a flat object), its scalar values are sent back on every
// load-more request of that search, so the endpoint can keep paging over the same
// result set. They never overwrite a param the request already has (search_url's
// own, the search term, skip, take), never set the reserved q/query/skip/take
// keys, and are forgotten when a new search starts or the selection is cleared.
const RESERVED_PAGE_PARAMS = new Set(["q", "query", "skip", "take"])

export default class extends ComboBoxController {
  static targets = [
    "searchIcon",
    "spinner",
    "optionsContainer",
    "sentinel",
    "loadMoreSpinner",
    "clearSelection",
    "avatarTemplate",
    "previewTemplate",
  ]

  static values = {
    searchUrl: String,
    take: { type: Number, default: 10 },
    displayAttribute: { type: String, default: "name" },
    initialItems: { type: Array, default: [] },
    unassignedLabel: String,
    placeholder: String,
    showEntityDetails: Boolean,
    noAddressLabel: String,
    fallbackAddressTooltip: String,
    showContactDetails: Boolean,
    requireEmail: Boolean,
    previewLabel: String,
    previewUrlTemplate: String,
  }

  connect() {
    super.connect()
    this._skip = 0
    this._total = null
    this._filter = ""
    this._loadingMore = false
    this._pageParams = {}
    this._debounceTimer = null
    this._setupObserver()
    this._syncClearButton()
  }

  disconnect() {
    super.disconnect()
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    if (this._observer) this._observer.disconnect()
  }

  // Reload a fresh preload page on every open (parity with the legacy ComboBox's
  // preload:"focus", mirrors the React getInitialOptions). super.open() clears the
  // search text via _resetFilter() but leaves the previous query's options in the
  // DOM, so reopening after a search must re-fetch the preload — otherwise the
  // dropdown shows stale rows from the prior query (CONEK-4920 regression fix).
  open() {
    super.open()
    this._performSearch("", { append: false })
  }

  // Override ComboBox's client-side filter with a debounced server-side search.
  filter() {
    if (this.hasClearSearchTarget) {
      this.clearSearchTarget.classList.toggle(
        "hidden",
        this.searchTarget.value.length === 0,
      )
    }
    if (this._debounceTimer) clearTimeout(this._debounceTimer)
    this._debounceTimer = setTimeout(() => {
      this._performSearch(this.searchTarget.value.trim(), { append: false })
    }, 300)
  }

  // --- Selection ---

  // Keep the clear button in sync whenever a value is committed (via click or
  // keyboard) on top of ComboBox's selection logic.
  _commitSelection(value, label) {
    super._commitSelection(value, label)
    this._unhoverLockedOptions()
    this._syncClearButton()
  }

  // Reset to the unselected (placeholder) state. Distinct from choosing the
  // "unassigned" option: this clears the field entirely and restores the muted
  // placeholder, mirroring the React ComboBox clear button.
  clearSelection(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    if (this.disabledValue) return
    this._pageParams = {}
    this.hiddenTarget.value = ""
    this.displayTarget.textContent = this.hasPlaceholderValue
      ? this.placeholderValue
      : ""
    this.displayTarget.classList.add("text-foreground-medium")
    this.triggerTarget.dataset.value = "false"

    this.optionTargets.forEach((option) => {
      option.setAttribute("aria-selected", "false")
      option.classList.remove(...this.SELECTED_CLASSES)
      option.classList.add(this.UNSELECTED_CLASS)
    })
    this._unhoverLockedOptions()

    this._syncClearButton()
    this.dispatch("change", { detail: { value: "", label: "" } })
    this.hiddenTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // Clear button is visible only while a non-empty value is selected — the
  // "unassigned" (empty) value has nothing to clear. Stays hidden on a disabled
  // field regardless of value, mirroring the server's show_clear_button? guard.
  _syncClearButton() {
    if (!this.hasClearSelectionTarget) return
    this.clearSelectionTarget.classList.toggle(
      "hidden",
      this.disabledValue || this.hiddenTarget.value === "",
    )
  }

  // --- Async fetching ---

  async _performSearch(query, { append = false } = {}) {
    if (this.disabledValue || !this.hasSearchUrlValue) return

    this._filter = query
    if (append) {
      if (this._loadingMore) return
      this._loadingMore = true
      this._setLoadMoreLoading(true)
    } else {
      this._setLoading(true)
    }

    const skip = append ? this._skip : 0
    if (!append) this._pageParams = {}

    try {
      const url = new URL(this.searchUrlValue, window.location.origin)
      // Send both param names: PackSupport::SearchParams endpoints read `query`,
      // others read `q`. `q` alone left the query nil server-side, so the task
      // picker returned unfiltered results (CONEK-4230).
      url.searchParams.set("q", query)
      url.searchParams.set("query", query)
      url.searchParams.set("take", this.takeValue)
      url.searchParams.set("skip", skip)
      if (append) this._mergePageParams(url.searchParams)

      const response = await get(url.toString(), {
        credentials: "include",
        responseKind: "json",
      })

      let items = []
      if (response.ok) {
        const data = await response.json
        const raw = Array.isArray(data)
          ? data
          : data.data || data.items || data.results || data.options || []
        this._total = Array.isArray(data) ? null : (data.total ?? null)
        if (!append && !Array.isArray(data)) {
          this._pageParams = this._scalarPageParams(data.page_params)
        }
        items = raw.map((it) => {
          const item = {
            value: String(it.id ?? it.value),
            label: String(
              it[this.displayAttributeValue] ??
                it.name ??
                it.label ??
                it.id ??
                it.value,
            ),
          }
          // CONEK-5661: the endpoint already returns these; only the Hotwire
          // renderer used to drop them. Same shape the React selectors build.
          if (this.showEntityDetailsValue) {
            item.badge = it.customerCode ?? null
            item.secondaryText = formatSiteAddressLine(it.address)
            item.showsFallbackAddress = showsFallbackAddress(it.address)
            item.hasDetails = true
          }
          // React ContactSelectItem reads the same fields, in the same order.
          if (this.showContactDetailsValue) {
            // No display attribute on the result: build the name like React
            // does, rather than falling through to the raw id.
            // An empty string counts as missing too (fullName: "" happens).
            if (!it[this.displayAttributeValue] && !it.name) {
              const parts = `${it.firstName ?? ""} ${it.lastName ?? ""}`.trim()
              if (parts) item.label = parts
            }
            item.jobTitle = it.jobTitle || null
            item.email = it.emails?.[0] ?? it.email ?? null
            item.pictureUrl = it.avatarUrl ?? it.pictureUrl ?? null
            item.disabled = this.requireEmailValue && !item.email
            item.hasContactDetails = true
          }
          return item
        })
      }

      if (append) {
        this._appendOptions(items)
        this._skip = skip + items.length
      } else {
        this._skip = items.length
        this._renderOptions(items)
        // Re-flip once the async results have grown the panel. open() runs
        // ComboBox#_position() while the panel is still empty (options load AFTER
        // open), so it anchors downward based on a near-zero height; without this
        // the dropdown then grows down and gets clipped to a couple of rows when
        // the field sits near the modal's bottom edge (CONEK-4230 QA). Re-running
        // it now that the options are in the DOM lets it flip above when needed.
        if (
          this.hasPanelTarget &&
          !this.panelTarget.classList.contains("hidden")
        ) {
          this._position()
        }
      }
    } catch {
      if (!append) this._renderOptions([])
    } finally {
      this._setLoading(false)
      this._setLoadMoreLoading(false)
      this._loadingMore = false
    }
  }

  // Only the scalar values of a flat object, stringified; anything else is ignored.
  _scalarPageParams(pageParams) {
    const out = {}
    if (!pageParams || typeof pageParams !== "object" || Array.isArray(pageParams))
      return out
    Object.entries(pageParams).forEach(([key, value]) => {
      if (["string", "number", "boolean"].includes(typeof value)) {
        out[key] = String(value)
      }
    })
    return out
  }

  _mergePageParams(params) {
    Object.entries(this._pageParams ?? {}).forEach(([key, value]) => {
      if (!RESERVED_PAGE_PARAMS.has(key) && !params.has(key)) params.set(key, value)
    })
  }

  _loadMore() {
    if (this._loadingMore) return
    if (this._total !== null && this._skip >= this._total) return
    this._performSearch(this._filter, { append: true })
  }

  // --- Option rendering ---

  _renderOptions(items) {
    // Preserve the selected option so it never disappears from the list after a
    // search (mirrors React's setOptionsPreservingCurrentValueOption).
    const selectedValue = this.hiddenTarget.value
    let list = items
    if (selectedValue && !list.some((i) => i.value === selectedValue)) {
      list = [
        {
          value: selectedValue,
          label: (this.displayTarget.textContent || selectedValue).trim(),
        },
        ...list,
      ]
    }

    // Prepend the "unassigned" option (empty value) so it is always available at
    // the top and never filtered out by the server search (mirrors the React
    // EntitySelector unassignedLabel). Guard against a stale empty-value row that a
    // preserved-selection or prior render may already have produced.
    if (this.hasUnassignedLabelValue && this.unassignedLabelValue) {
      list = [
        { value: "", label: this.unassignedLabelValue, unassigned: true },
        ...list.filter((i) => i.value !== ""),
      ]
    }

    this.optionsContainerTarget.innerHTML = ""
    list.forEach((item) =>
      this.optionsContainerTarget.appendChild(this._buildOption(item)),
    )
    // Empty state reflects the fetched results only — the unassigned option and a
    // preserved selection are controls, not search matches.
    this._updateEmpty(items.length)
    this._activeIndex = -1
  }

  _appendOptions(items) {
    const existing = new Set(this.optionTargets.map((o) => o.dataset.value))
    items
      .filter((item) => !existing.has(item.value))
      .forEach((item) =>
        this.optionsContainerTarget.appendChild(this._buildOption(item)),
      )
  }

  _buildOption(item) {
    const selected = this.hiddenTarget.value === item.value
    const el = document.createElement("div")
    el.setAttribute("role", "option")
    el.setAttribute("aria-selected", selected ? "true" : "false")
    el.setAttribute("tabindex", "-1")
    el.dataset.value = item.value
    el.dataset.label = item.label
    if (item.unassigned) el.dataset.unassigned = "true"
    el.setAttribute("data-ui--entity-selector-target", "option")
    el.setAttribute("data-action", "click->ui--entity-selector#select")
    el.className = this._optionClass(selected)
    // `hasDetails` is set by the fetch mapper only. The unassigned option and a
    // preserved selection are synthetic controls with no company behind them, so
    // they fall through to the plain label — rendering the rich row for those is
    // what blank-paged the Tasks list in CONEK-5661.
    if (item.hasDetails) {
      el.appendChild(this._buildDetailRow(item))
    } else if (item.hasContactDetails) {
      if (item.disabled) {
        el.setAttribute("aria-disabled", "true")
        el.classList.add("cursor-not-allowed")
        // Not pickable, so no hover highlight either.
        el.classList.remove("hover:bg-input")
      }
      el.appendChild(this._buildContactRow(item))
    } else {
      el.textContent = item.label
    }
    return el
  }

  // Two-line option row: name + right-aligned customer code, address line below.
  // Mirrors shared/components/customer-option-row.tsx so the Hotwire and React
  // selectors are visually identical (CONEK-5661 / CONEK-6128).
  _buildDetailRow(item) {
    const row = document.createElement("div")
    row.className = "flex w-full flex-col items-start overflow-hidden"

    const top = document.createElement("div")
    top.className = "flex w-full items-center gap-2"

    const name = document.createElement("span")
    name.className = "flex-1 truncate"
    name.textContent = item.label
    top.appendChild(name)

    if (item.badge) {
      const badge = document.createElement("span")
      badge.className =
        "bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-xs font-medium"
      badge.textContent = item.badge
      top.appendChild(badge)
    }
    row.appendChild(top)

    const address = document.createElement("span")
    address.className = "text-muted-foreground w-full truncate text-xs"
    address.textContent = item.secondaryText || this.noAddressLabelValue
    // React shows an info-icon tooltip here; a title attribute carries the same
    // warning without pulling a tooltip component into JS-built DOM.
    if (item.showsFallbackAddress && this.hasFallbackAddressTooltipValue) {
      address.title = this.fallbackAddressTooltipValue
    }
    row.appendChild(address)

    return row
  }

  // Contact-person row: avatar, optional job title, name, email, and an eye
  // button. Mirrors shared/components/contact-select-item.tsx; the avatar and the
  // eye come from server-rendered <template>s so they stay design-system markup.
  _buildContactRow(item) {
    const row = document.createElement("div")
    row.className = "flex w-full min-w-0 items-center gap-2"

    const content = document.createElement("div")
    content.className = "flex min-w-0 flex-1 items-center gap-2"
    // Like React, a disabled row fades its content but not the row itself.
    if (item.disabled) content.classList.add("opacity-50")

    const avatar = this._buildAvatar(item)
    if (avatar) content.appendChild(avatar)

    const text = document.createElement("div")
    text.className = "flex min-w-0 flex-1 flex-col justify-center"
    if (item.jobTitle) {
      const title = document.createElement("p")
      title.className = "text-foreground-medium truncate text-xs font-medium"
      title.textContent = item.jobTitle
      text.appendChild(title)
    }
    const name = document.createElement("span")
    name.className = "text-foreground truncate text-base font-medium"
    name.textContent = item.label
    text.appendChild(name)
    if (item.email) {
      const email = document.createElement("p")
      email.className = "text-foreground truncate text-sm font-normal"
      email.textContent = item.email
      text.appendChild(email)
    }
    content.appendChild(text)
    row.appendChild(content)

    // React drops the eye on a disabled row too: nothing to act on there.
    if (!item.disabled) {
      const eye = this._buildPreviewButton(item)
      if (eye) row.appendChild(eye)
    }
    return row
  }

  _buildAvatar(item) {
    if (!this.hasAvatarTemplateTarget) return null
    const avatar =
      this.avatarTemplateTarget.content.firstElementChild?.cloneNode(true)
    if (!avatar) return null
    // Swap the template's (empty) initials text for this contact's.
    Array.from(avatar.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .forEach((node) => node.remove())
    avatar.prepend(document.createTextNode(this._initials(item.label)))
    const img = avatar.querySelector("img")
    if (img) {
      if (item.pictureUrl) {
        img.src = item.pictureUrl
        img.alt = ""
      } else {
        img.remove()
      }
    }
    return avatar
  }

  // Same derivation as Ui::ProfilePictureComponent#display_initials.
  _initials(name) {
    const words = (name || "").trim().split(/\s+/).filter(Boolean)
    if (!words.length) return ""
    const raw =
      words.length >= 2
        ? words[0][0] + words[1][0]
        : words[0].slice(0, 2)
    return raw.toUpperCase()
  }

  _buildPreviewButton(item) {
    if (!this.hasPreviewTemplateTarget) return null
    const button =
      this.previewTemplateTarget.content.firstElementChild?.cloneNode(true)
    if (!button) return null
    if (this.previewLabelValue) {
      button.setAttribute(
        "aria-label",
        // A function replacement, so "$&"-style patterns in a name stay literal.
        this.previewLabelValue.replace("__NAME__", () => item.label),
      )
    }
    const url = this._previewUrl(item.value)
    if (url) button.dataset.previewUrl = url
    button.addEventListener("click", (event) => this._preview(event, item, url))
    return button
  }

  _previewUrl(id) {
    if (!this.hasPreviewUrlTemplateValue || !this.previewUrlTemplateValue)
      return null
    return this.previewUrlTemplateValue.replace("__ID__", encodeURIComponent(id))
  }

  // The eye sits inside the option, whose click selects — stop it there. What
  // the preview opens is the host's call; by default the popover closes so that
  // preview paints on top, unless a listener cancels the event.
  _preview(event, item, url) {
    event.preventDefault()
    event.stopPropagation()
    // Only the public contact fields — the controller's internal row flags are
    // not part of the event contract.
    const contact = {
      id: item.value,
      label: item.label,
      email: item.email ?? null,
      jobTitle: item.jobTitle ?? null,
      pictureUrl: item.pictureUrl ?? null,
    }
    const previewEvent = this.dispatch("preview", {
      detail: { id: item.value, item: contact, url },
      cancelable: true,
    })
    if (!previewEvent.defaultPrevented) {
      this.close()
      // Focus was on the eye, which the popover just hid. Browsers keep
      // activeElement on a node inside a display:none container, so check for
      // that as well as <body>; a host preview that took focus is left alone.
      const active = document.activeElement
      const lost =
        !active ||
        active === document.body ||
        (this.hasPanelTarget && this.panelTarget.contains(active))
      if (lost && this.hasTriggerTarget) this.triggerTarget.focus()
    }
  }

  // ComboBox re-adds the hover class to every unselected option on each
  // selection change; a locked contact must not get it back.
  _unhoverLockedOptions() {
    this.optionTargets
      .filter((option) => option.getAttribute("aria-disabled") === "true")
      .forEach((option) => option.classList.remove(this.UNSELECTED_CLASS))
  }

  // A disabled contact (require_email without an email) cannot be picked by
  // click…
  select(event) {
    if (event.currentTarget?.getAttribute("aria-disabled") === "true") return
    super.select(event)
  }

  // …nor reached by the arrow keys, the same as a disabled React command item.
  _visibleOptions() {
    return super
      ._visibleOptions()
      .filter((option) => option.getAttribute("aria-disabled") !== "true")
  }

  // Enter on a focused eye button is a click on it, not a pick of the row the
  // arrow keys last highlighted.
  _handleKeydown(event) {
    if (
      event.key === "Enter" &&
      event.target?.closest?.("[data-entity-selector-preview]")
    )
      return
    super._handleKeydown(event)
  }

  _optionClass(selected) {
    const base =
      "relative flex cursor-default select-none items-center text-sm outline-none py-3 px-3 rounded-sm"
    return selected
      ? `${base} bg-primary/20! text-primary hover:bg-primary/20!`
      : `${base} hover:bg-input`
  }

  _updateEmpty(count) {
    if (this.hasEmptyTarget) {
      this.emptyTarget.classList.toggle("hidden", count > 0)
    }
  }

  // --- Loading indicators ---

  _setLoading(loading) {
    if (this.hasSearchIconTarget)
      this.searchIconTarget.classList.toggle("hidden", loading)
    if (this.hasSpinnerTarget)
      this.spinnerTarget.classList.toggle("hidden", !loading)
  }

  _setLoadMoreLoading(loading) {
    if (this.hasLoadMoreSpinnerTarget) {
      this.loadMoreSpinnerTarget.classList.toggle("hidden", !loading)
    }
  }

  // --- Infinite scroll ---

  _setupObserver() {
    if (!this.hasSentinelTarget || typeof IntersectionObserver === "undefined")
      return
    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && this._isOpen) this._loadMore()
        })
      },
      { threshold: 1.0 },
    )
    this._observer.observe(this.sentinelTarget)
  }
}
